import { createApp, unref } from 'vue'
import App from './App.vue'
import './registerServiceWorker'
import router from './router'
import pinia from './stores'
import CoreuiVuePro from '@coreui/vue-pro'
import CIcon from '@coreui/icons-vue'
import { iconsSet as icons } from './assets/icons/icons.js'
import Toast from 'vue-toastification'
import 'vue-toastification/dist/index.css'
import { useVuelidate } from '@vuelidate/core'
import { Money3Directive } from 'v-money3'
import responsiveTable from '@/directives/responsive-table'
import safeTooltip from '@/directives/safe-tooltip'

import ButtonLoading from '@/components/common/ButtonLoading.vue'
import CodeContainer from '@/components/common/CodeContainer.vue'
import BooleanBadge from '@/components/common/BooleanBadge.vue'
import DualListBox from '@/components/common/DualListBox.vue'

import titleMixin from '@/mixins/title-mixin'
import { REQUIRED_FIELD_ERROR_MESSAGE } from '@/utils'

const app = createApp(App)

const debug = import.meta.env.DEV

if (!debug) {
  // Lazy-load Sentry so it lands in its own async chunk instead of the initial
  // vendors bundle. It still initializes on startup, just not inline.
  import('@sentry/vue').then((Sentry) => {
    Sentry.init({
      app,
      environment: import.meta.env.VUE_APP_SENTRY_ENVIRONMENT,
      dsn:
        'https://75d63a109a00485691ebc49abb6fa5c4@o70049.ingest.sentry.io/5655937',
      integrations: [Sentry.browserTracingIntegration()],
      tracesSampleRate: 1.0
    })
  })
}

app.use(pinia)
app.use(router)
app.use(Toast, {
  position: 'top-right',
  timeout: 5000,
  closeOnClick: true,
  pauseOnFocusLoss: true,
  pauseOnHover: true,
  draggable: true,
  draggablePercent: 0.6,
  showCloseButtonOnHover: false,
  hideProgressBar: false,
  closeButton: 'button',
  icon: true,
  rtl: false
})
app.use(CoreuiVuePro)

// Override CoreUI's v-c-tooltip with a null-safe wrapper (see safe-tooltip.js).
// Drop CoreUI's registration first so re-registering doesn't warn in dev mode.
delete app._context.directives['c-tooltip']
app.directive('c-tooltip', safeTooltip)

// v-money3 is the Vue 3 fork of v-money. Register its directive under the
// original `money` name so existing `v-money="config"` bindings keep working.
app.directive('money', Money3Directive)

// Responsive tables: stamp column labels onto cells so CoreUI tables can
// collapse into labeled cards on small screens (see components/_table.scss).
app.directive('responsive-table', responsiveTable)

app.component('ButtonLoading', ButtonLoading)
app.component('CodeContainer', CodeContainer)
app.component('BooleanBadge', BooleanBadge)
app.component('DualListBox', DualListBox)

// CIcon moved to @coreui/icons-vue in CoreUI v5. Register it globally and
// provide the icon set so `<CIcon icon="cil-..." />` usages resolve.
app.component('CIcon', CIcon)
app.provide('icons', icons)

app.mixin(titleMixin)

// Vuelidate v2 Options API support: components keep declaring a `validations()`
// option and read the collected state as `v$` (the `$`-prefixed `$v` name is
// reserved by Vue 3). A global mixin `setup()` return is NOT merged onto the
// instance in Vue 3, so `this.v$` was always undefined. Instead call
// `useVuelidate()` in `beforeCreate` (where the instance scope is active) and
// expose it as an unwrapped, reactive getter.
//
// `$scope: false` keeps each component's `v$` strictly its own, matching
// Vuelidate 1 behavior. Vuelidate 2's default scope bubbles child results into
// ancestors, so e.g. a nested list's (legitimately) invalid edit-form state
// would permanently disable an unrelated parent form's submit button. No
// component here relies on cross-component collection — each `v$` consumer
// declares its own `validations`.
app.mixin({
  beforeCreate() {
    const result = useVuelidate({ $scope: false })
    Object.defineProperty(this, 'v$', {
      configurable: true,
      get: () => unref(result)
    })
  }
})

app.mixin({
  methods: {
    getSafe(fn, defaultValue) {
      try {
        const val = fn()
        return val != null ? val : defaultValue
      } catch (e) {
        return defaultValue
      }
    },

    handleURLProtocol(url) {
      if (/^https?:\/\//i.test(url)) {
        return url
      }

      return url ? `//${url}` : '#'
    }
  }
})

// Expose app-wide constants on every component (templates and `this`).
app.config.globalProperties.REQUIRED_FIELD_ERROR_MESSAGE = REQUIRED_FIELD_ERROR_MESSAGE
app.config.globalProperties.CLIENT_SIDE_BASE_URL =
  import.meta.env.VUE_APP_CLIENT_SIDE_BASE_URL

if (window.Cypress) {
  // only available during E2E tests
  window.app = app
}

app.mount('#app')
